import { type Ref, computed, ssrRef } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { backendToLocaleMap, localeToBackendMap } from "@/config";
import { type Settings, type SettingsStore } from "@/types";

import useSettingsApi from "./-api";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function    useSettingsStore
 *
 * @desc        A pinia store for interacting with the settings API. The functions
 *              are wrappers around the useApi composable's request function. The
 *              returned functions CANNOT be destructured without losing reactivity.
 *
 * @summary     A composable that exposes the settings store.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Usage of `useSettingsStore`</caption>
 * ```ts
 *   const settingsStore = useSettingsStore();
 *
 *   // ❌ Do not destructure the returned functions
 *   const { get, put } = settingsStore;
 *
 *   // ✅ Instead, use the returned functions directly
 *   const { data: settings } = await settingsStore.get();
 *   const { data: updatedSettings } = await settingsStore.put({ email: "" });
 * ```
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useSettingsStore = defineStore(
  "settings",
  () => {
    const _settings: Ref<Settings | null> = ssrRef(null, "settings");

    const settings = computed({
      get: () => _settings.value,
      set: (val) => {
        if (val) {
          // Convert data to frontend format
          val.locale = backendToLocaleMap[val.locale] ?? val.locale;
          _settings.value = val;
        }
      },
    });

    const fetchSettings: SettingsStore["fetchSettings"] = async function () {
      const response = await this.$api.get();

      settings.value = response.data.settings;
    };

    const updateSettings: SettingsStore["updateSettings"] = async function updateSettings(val: Partial<Settings>) {
      // Convert data to backend format
      val.locale && (val.locale = localeToBackendMap[val.locale] ?? val.locale);

      const response = await this.$api.put(val);

      settings.value = response.data.settings;
    };

    const $reset = function () {
      _settings.value = null;
    };

    return {
      settings,
      fetchSettings,
      updateSettings,
      $reset,
    };
  },
  {
    api: useSettingsApi,
  },
);
